// tslint:disable:max-func-body-length
import { AuthType, ConsoleLogger, getAuthType, getErrorAsString, IAuthenticatedUserInfo, ILogger, isMsa, logAndBuildError } from '@notes/webintegrations-common'
import { loadTheme } from 'office-ui-fabric-react/lib/Styling'
import { Store } from 'redux'
import { AppIntegrationSetup } from './AppIntegrationSetup'
import { STICKY_NOTES_CANVAS_ID } from './components/App/App'
import { createHubLayoutSDKWrapper } from './createHubLayoutSDKWrapper'
import { msaImplicitAuthFlow } from './init/msaImplicitAuthFlow'
import { o365AuthFlow } from './init/o365AuthFlow'
import { LayoutSDKWrapper } from './layoutSDK/LayoutSDKWrapper'
import { LocalizationService } from './localization/LocalizationService'
import { setExportDialogVisibilityAction } from './redux/actions/exportDialog'
import { createClientStore } from './redux/createClientStore'
import { IState } from './redux/state'
import { render } from './render'
import { GetTokenFactory } from './shared/auth/GetTokenFactory'
import { InitialTokenFetchActivity } from './shared/auth/InitialTokenFetchActivity'
import { USE_IMPLICIT_AUTH_FOR_MSA } from './shared/auth/useImplicitAuthForMsa'
import { clearCaches } from './shared/caches'
import { ClientPerfMetrics, PerfMetricEvents } from './shared/ClientPerfMetrics'
import { GlobalVariables } from './shared/GlobalVariables'
import { ApiUtils } from './shared/lib/apiUtils'
import { getO365AuthToken } from './shared/lib/AuthTokenUtils'
import { UserInfoApiClient } from './shared/lib/UserInfoApiUtils'
import { hideLoadingSpinner } from './shared/LoadingSpinner'
import { Log } from './shared/logger/Log'
import { shouldMockExternalApis } from './shared/shouldMockExternalApis'
import { applyShell } from './shell/applyShell'
import { windowObject } from './windowObject'

import { IUserFlowInformation } from './init/IUserFlowInformation'
import { redirectToAuth } from './init/redirectToAuth'
import { enableDarkThemeForWebApp } from './lib/themeUtils'
import { isEdgeHubQueryParamPresent, isMsaAccountLogin, isServiceWorkerEnabled } from './lib/urlParserUtils'
import { Constants } from './shared/Constants'
import { FlushNewUserTelemetry } from './shared/FlushNewUserTelemetry'
import { logIfNewShorelineFeedUserFromLocalStorage } from './shared/lib/newUserTelemetryUtils'
import { applyDefaultPolicy, applyScriptUrlPolicy } from './shared/lib/trustedTypesPolicy'
import { CommonLogger } from './shared/logger/CommonLogger'
import { LoggerNamespace } from './shared/logger/LoggerNamespace'
import { supportsCSSVariables } from './shared/supportsCSSVariables'
import './shared/webIntegrationTweaks.css'

/**
 * This file requires all files needed to build the sticky notes 'frame' - the frame is the O365 header + the sticky notes app
 */
const clientPerfMetrics = ClientPerfMetrics.create()
const isEdgeHub: boolean = isEdgeHubQueryParamPresent()
const shouldEnableDarkMode: boolean = window.matchMedia('(prefers-color-scheme: dark)').matches
const SERVICE_WORKER_URL = 'https://www.onenote.com/memory/service-worker'

function createOnSignOutHandler(layoutSDKWrapper: LayoutSDKWrapper): () => void {
  return () => {
    clearCaches()
    layoutSDKWrapper.layoutSDK.signOut()
  }
}

function createOnExportNotesClickCallback(clientStore: Store<IState>): () => void {
  return () => {
    clientStore.dispatch(setExportDialogVisibilityAction(true))
  }
}

// Performs auth setup, returns the user information, and whether we should stop execution
async function setupAuthAndGetInitialUserInfo(isMsaAccount: boolean): Promise<IUserFlowInformation> {
  const initialTokenFetchActivity = new InitialTokenFetchActivity()

  if (shouldMockExternalApis()) {
    initialTokenFetchActivity.finishWithSuccess('MockAuth', 'MockUser')
    return {
      shouldStopExecution: false,
      userInfo: undefined,
    }
  }

  const authType: AuthType | undefined = getAuthType(USE_IMPLICIT_AUTH_FOR_MSA)
  if (!authType) {
    initialTokenFetchActivity.finishWithSuccess(authType, 'RedirectedToSignIn')

    // Enabling MSA SSO only for Edge Hub apps
    return redirectToAuth(isEdgeHub && isMsaAccount)
  }

  if (GlobalVariables.environmentSettings.RedirectToOutlookNotesFolder) {
    if (isMsa(authType)) {
      window.location.href = 'https://outlook.live.com/mail/notes'
    } else {
      window.location.href = 'https://outlook.office.com/mail/notes'
    }
    return {
      shouldStopExecution: true,
      userInfo: undefined,
    }
  }

  // These helper methods will finish the initialTokenFetchActivity
  if (isMsa(authType)) {
    return await msaImplicitAuthFlow(initialTokenFetchActivity)
  }
  return await o365AuthFlow(initialTokenFetchActivity)
}

function loadEdgeHubTheme(): void {
  require('./edgeHubTweaks.css')
  loadTheme({
    palette: {
      // addnote hover
      themePrimary: 'var(--oneNotePurple)',
      // filter hover
      themeDarkAlt: 'var(--oneNotePurple)',
      // filter active
      themeDark: 'var(--oneNotePurple)',
    },
  })
}

async function startApp(): Promise<void> {
  const namespace: LoggerNamespace = 'StickyNotes'
  let commonLogger: CommonLogger | null = null
  let logger: ILogger | undefined =  undefined

  if (shouldMockExternalApis()) {
    logger = new ConsoleLogger(namespace)
  } else {
    commonLogger = new CommonLogger(namespace)
    logger = commonLogger
  }

  AppIntegrationSetup.initLogging(window, logger)
  Log.logger.logReportData('AppBootStarted', false /* isCriticalEvent */)

  const isMsaAccount: boolean = isMsaAccountLogin()
  const userFlowInformation = await setupAuthAndGetInitialUserInfo(isMsaAccount)
  if (userFlowInformation.shouldStopExecution) {
    clientPerfMetrics.addMeasurement(PerfMetricEvents.RedirectedToLogin)
    clientPerfMetrics.log()
    return
  }
  const userInfo: IAuthenticatedUserInfo | undefined = userFlowInformation.userInfo

  if (userInfo && commonLogger) {
    commonLogger.setUserContext(userInfo)
  }

  if (isEdgeHub) {
    applyDefaultPolicy()
    applyScriptUrlPolicy()
    document.addEventListener(Constants.NEW_USER_TO_ON_ECOSYSTEM_TELEMETRY_FLUSH_EVENT, FlushNewUserTelemetry.eventHandler)
  }
  if (supportsCSSVariables()) {
    if (shouldEnableDarkMode) {
      enableDarkThemeForWebApp()
    }
    if (isEdgeHub) {
      loadEdgeHubTheme()
    }
  }

  clientPerfMetrics.addMeasurement(PerfMetricEvents.UserObtained)

  const store = createClientStore()

  // determine initial screen size and register for additional screen size changes
  windowObject.detectScreenSize(store)
  windowObject.listenForResize(store)

  await AppIntegrationSetup.init('StickyNotesFrameBoot', GlobalVariables.settingsForClient.Locale, userInfo)
  const authType = userInfo ? userInfo.authType : undefined
  const showHeader = !isEdgeHub
  render(store, authType, showHeader)

  const layoutSDKWrapper = createHubLayoutSDKWrapper(store, userInfo, isEdgeHub)
  hideLoadingSpinner()
  if (showHeader) {
    // We intentionally kick off the shell promise without awaiting
    const applyShellPromise = applyShell(createOnSignOutHandler(layoutSDKWrapper), createOnExportNotesClickCallback(store), userInfo)
    await setupAndLoginUsingLayoutSDKWrapper(layoutSDKWrapper)
    await applyShellPromise
  } else {
    await setupAndLoginUsingLayoutSDKWrapper(layoutSDKWrapper)
  }
  clientPerfMetrics.addMeasurement(PerfMetricEvents.Rendered)
  clientPerfMetrics.addMeasurement(PerfMetricEvents.ShellRendered)

  // Log client side metrics
  clientPerfMetrics.log()
  // Log server side metrics
  Log.logger.logReportData('StickyNotesServerPerformance', true /* isCriticalEvent */, GlobalVariables.settingsForClient.ServerSidePerfMetrics)
  if (isEdgeHub) {
    logIfNewShorelineFeedUserFromLocalStorage()
    // tslint:disable-next-line: no-floating-promises
    // TODO: uncomment below code once we have MSA permission for OneNote API
    // logFREUsers(authType)
  }
  if (isEdgeHub && isServiceWorkerEnabled()) {
    setupServiceWorker()
  }
}

function setupServiceWorker(): void {
  if ('serviceWorker' in navigator) {
    setTimeout(() => {
      navigator.serviceWorker.register(SERVICE_WORKER_URL).then(() => {
        Log.logger.info(`Registering service worker.`)
      }).catch((err: Error) => {
        Log.logger.warn(`Error registering service worker: ${getErrorAsString(err)}.`)
      })
    }, 6000)
  }
}

// @ts-ignore
async function logFREUsers(authType: AuthType | undefined): Promise<void> {
  if (!authType) {
    Log.logger.warn('Auth type is undefined. Aborting logFREUsers!')
    return
  }

  try {
    let token
    if (isMsa(authType)) {
      const tokenGetter = GetTokenFactory.createMsa(false /* forceRefresh */)
      token = await tokenGetter.getOneNoteUserInfoToken()
      if (!token) {
        Log.logger.warn('MSA access token is undefined. Aborting logFREUsers!')
        return
      }
    } else {
      const tokenInfo = await getO365AuthToken()
      if (!tokenInfo || !tokenInfo.access_token) {
        Log.logger.warn('o365 access token is undefined. Aborting logFREUsers!')
        return
      }
      token = tokenInfo.access_token
    }

    const userInfoClient: UserInfoApiClient = new UserInfoApiClient(ApiUtils.buildOneNoteApi(token))
    await userInfoClient.logIfNewShorelineFeedUser()
  } catch (error) {
    Log.logger.warn(`Error occurred while logging FRE users: ${getErrorAsString(error)}. Aborting logFREUsers!`)
    return
  }
}

async function setupAndLoginUsingLayoutSDKWrapper(layoutSDKWrapper: LayoutSDKWrapper): Promise<void> {
  await layoutSDKWrapper.setup({ renderElementId: STICKY_NOTES_CANVAS_ID, langCode: LocalizationService.locale, theme: shouldEnableDarkMode ? 'Dark' : 'Light' })
  await layoutSDKWrapper.login()
}

startApp().catch((error: Error) => {
  throw logAndBuildError(`Exception thrown in startApp! ${getErrorAsString(error)}`)
})
